import type { SimulatorArea } from '@vedalib/editor/lib/elements'
import type { RichTextValue } from '@vedalib/rich-text'
import { textToRtValue } from '@vedalib/rich-text'
import cn from 'classnames'
import React from 'react'

import RichText from 'components/form/RichText/RichText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { stopPropagation } from 'constants/commonConstans'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'

import { useSimulatorContext } from '../SimulatorContext'
import CloseIconStyled from '../styled/CloseIconStyled'
import * as s from './HintContent.module.scss'

interface IHintContentProps {
  id: string
  index?: number
  area: SimulatorArea
  onClose: (e: React.MouseEvent | React.KeyboardEvent) => void
}

const HintContent = ({ id, area, onClose }: IHintContentProps) => {
  const styles = useSimulatorContext((c) => c.styles)
  const font = useSimulatorContext((c) => c.font)
  const mode = useSimulatorContext((c) => c.mode)
  const onChange = useSimulatorContext((c) => c.onChange)
  const waiting = useSimulatorContext((c) => c.waiting)
  const value = useSimulatorContext((c) => c.value)
  const elementId = useSimulatorContext((c) => c.elementId) || ''
  const isDemoMode = mode?.previewMode === PreviewMode.demo
  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })

  const onChangeLabel = (labelValue: RichTextValue) => {
    if (!value || !onChange) {
      return
    }

    const updatedItems = value.items.map((item) => {
      if (!item.areas) {
        return item
      }

      const updatedAreas = item.areas.map((area) => {
        if (area.id === id) {
          return { ...area, hint: { ...area.hint, text: labelValue } } as SimulatorArea
        }
        return area
      })

      return { ...item, areas: updatedAreas }
    })

    onChange({
      ...value,
      items: updatedItems,
    })
  }

  const hintName = `${area.id}.hint`

  const handleCloseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClose(e)
    }
  }

  return (
    <div
      className={s.root}
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      role='tooltip'
      style={{
        borderRadius: 'var(--brand-borderRadius)',
        backgroundColor: styles.hint?.backgroundColor,
      }}
      data-simulator-area-hint
    >
      {area.hint?.indicator && (
        <div
          className={cn(s.indicator, area.hint?.position && s[area.hint?.position])}
          style={{ color: styles.hint?.backgroundColor }}
        >
          <Icon name='polygon' aria-hidden />
        </div>
      )}
      <div className={cn(s.content, s[mode.deviceMode])}>
        <span
          onMouseDown={(e) => {
            e.stopPropagation()
            onLabelSelect(hintName)
          }}
        >
          <RichText
            active={isActiveElement && activeIndex === hintName}
            disabled={!isFill}
            isPreview={mode?.previewMode !== PreviewMode.editor}
            name={hintName}
            onChange={onChangeLabel}
            placeholder={t('elements.simulator.placeholder')}
            styles={font}
            value={area.hint?.text || textToRtValue('')}
            waiting={waiting}
            autofocus
          />
        </span>
      </div>
      {area.hint?.showCloseButton && (
        <CloseIconStyled
          $styles={styles}
          aria-label='Close hint'
          onKeyDown={!isDemoMode ? handleCloseKeyDown : undefined}
          onMouseDown={!isDemoMode ? onClose : undefined}
        >
          {!styles.hint?.backgroundImage && <Icon name='otherClose' size={KitSize.S} aria-hidden />}
        </CloseIconStyled>
      )}
    </div>
  )
}

export default HintContent
